// frontend/src/components/MainContent.tsx
import { useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import { recheckLandingProfile, useApprovalsDeskAccess } from '../hooks/useLandingProfile';
import { PullToRefreshProvider } from '../contexts/PullToRefreshContext';

const Home = lazy(() => import('./home/Home'));
const ApprovalsDesk = lazy(() => import('./home/ApprovalsDesk'));
const UserManagement = lazy(() => import('./settings/UserManagement'));
const RoleCatalog = lazy(() => import('./settings/RoleCatalog'));
const NotificationSettings = lazy(() => import('./settings/NotificationSettings'));
const HomepageContentAdmin = lazy(() => import('./settings/HomepageContentAdmin'));
const PublicHolidaysAdmin = lazy(() => import('./settings/PublicHolidaysAdmin'));
const UserSettings = lazy(() => import('./settings/UserSettings'));

// Procurement Estimates
const ProcurementEstimates = lazy(() => import('./jobs/estimates/ProcurementEstimates'));
const ProcurementEstimatesList = lazy(() => import('./jobs/estimates/ProcurementEstimatesList'));
const ProcurementEstimateDetail = lazy(() => import('./jobs/estimates/ProcurementEstimateDetail'));

// Client Requests
const ProcurementClientRequestForm = lazy(() => import('./procurement/client-requests/ProcurementClientRequestForm'));
const ProcurementClientRequestList = lazy(() => import('./procurement/client-requests/ProcurementClientRequestList'));
const ProcurementClientRequestDetail = lazy(() => import('./procurement/client-requests/ProcurementClientRequestDetail'));

// Client Invoices
const ClientInvoiceForm = lazy(() => import('./financial/invoices/ClientInvoiceForm'));
const ClientInvoiceList = lazy(() => import('./financial/invoices/ClientInvoiceList'));
const ClientInvoiceDetail = lazy(() => import('./financial/invoices/ClientInvoiceDetail'));

// Jobs
const JobForm = lazy(() => import('./jobs/JobForm'));
const JobList = lazy(() => import('./jobs/JobList'));
const JobDetail = lazy(() => import('./jobs/JobDetail'));
const JobsAdmin = lazy(() => import('./jobs/JobsAdmin'));

// Rates
const RatesDetail = lazy(() => import('./financial/rates/RatesDetail'));
const Rates = lazy(() => import('./financial/rates/Rates'));

// Expenses
const ExpenseEntryForm = lazy(() => import('./financial/expenses/ExpenseEntryForm'));
const ExpenseList = lazy(() => import('./financial/expenses/ExpenseList'));
const ExpenseDetail = lazy(() => import('./financial/expenses/ExpenseDetail'));
const ExpenseApprovals = lazy(() => import('./financial/expenses/ExpenseApprovals'));
const ExpenseAdmin = lazy(() => import('./financial/expenses/ExpenseAdmin'));
const SharedCardAiLog = lazy(() => import('./financial/expenses/SharedCardAiLog'));
const ReceiptAutoMatchDashboard = lazy(() => import('./financial/expenses/ReceiptAutoMatchDashboard'));
const ExpensesDashboard = lazy(() => import('./financial/expenses/ExpensesDashboard'));
const ManualOfAuthority = lazy(() => import('./financial/admin/ManualOfAuthority'));
const FinancialReportsHome = lazy(() => import('./financial/reporting/FinancialReportsHome'));
const FinancePnlReport = lazy(() => import('./financial/pnl/FinancePnlReport'));
const ChartOfAccountsBrowser = lazy(() => import('./financial/reporting/ChartOfAccountsBrowser'));
const JobFinancialDashboard = lazy(() => import('./financial/reporting/JobFinancialDashboard'));

// Project Controls
const Templates = lazy(() => import('./projectControls/templates/Templates'));
const TemplateBuilder = lazy(() => import('./projectControls/templates/TemplateBuilder'));
const Proposals = lazy(() => import('./projectControls/proposals/Proposals'));
const ProposalDetail = lazy(() => import('./projectControls/proposals/ProposalDetail'));

// Approvals

// Requisitions
const RequisitionEntryForm = lazy(() => import('./procurement/requisitions/RequisitionEntryForm'));
const RequisitionList = lazy(() => import('./procurement/requisitions/RequisitionList'));
const RequisitionDetail = lazy(() => import('./procurement/requisitions/RequisitionDetail'));
const PurchaseOrdersTabs = lazy(() => import('./procurement/requisitions/PurchaseOrdersTabs'));
const ProcurementJobs = lazy(() => import('./procurement/jobs/ProcurementJobs'));
const PurchaseOrderApprovals = lazy(() => import('./procurement/requisitions/PurchaseOrderApprovals'));
const PurchaseOrderReceipting = lazy(() => import('./procurement/requisitions/PurchaseOrderReceipting'));
const HeadcountReport = lazy(() => import('./reports/HeadcountReport'));
const EARequirementsReport = lazy(() => import('./reports/EARequirementsReport'));
const MedicalsReport = lazy(() => import('./reports/MedicalsReport'));
const CostTransactionsReport = lazy(() => import('./reports/CostTransactionsReport'));

// HR
const HrOnboardingForm = lazy(() => import('./HR/onboarding/HrOnboardingForm'));
const HrOnboardingList = lazy(() => import('./HR/onboarding/HrOnboardingList'));
const HRWorkItemsPage = lazy(() => import('./HR/HRWorkItemsPage'));
const PayslipsSelfService = lazy(() => import('./HR/PayslipsSelfService'));
const OrgChart = lazy(() => import('./HR/OrgChart'));
const HRAdmin = lazy(() => import('./HR/admin/HRAdmin'));
const HREmployeeAdmin = lazy(() => import('./HR/employee/HREmployeeAdmin'));
const HREmployeesList = lazy(() => import('./HR/employee/HREmployeesList'));
const HREmployeeDetail = lazy(() => import('./HR/employee/HREmployeeDetail'));
const HRInsightsDashboard = lazy(() => import('./HR/HRInsightsDashboard'));
const HRPerformance = lazy(() => import('./HR/performance/HRPerformance'));
const HRSitesAndPositions = lazy(() => import('./HR/admin/HRSitesAndPositions'));

// Rosters
const Rosters = lazy(() => import('./rosters/Rosters'));

// Timesheets
const ApiErrors = lazy(() => import('./timesheets').then(m => ({ default: m.ApiErrors })));
const TimesheetPage = lazy(() => import('./timesheets/TimesheetPage'));

// Santos / Fieldglass admin
const SantosFGAdmin = lazy(() => import('./santosFG/SantosFGAdmin'));

// Learning (LMS)
const LmsCourseAdmin = lazy(() => import('./lms/LmsCourseAdmin'));
const LmsCourseDetail = lazy(() => import('./lms/LmsCourseDetail'));
const LmsAdmin = lazy(() => import('./lms/LmsAdmin'));
const LmsCoursePlayer = lazy(() => import('./lms/LmsCoursePlayer'));
const LmsCourseBuilder = lazy(() => import('./lms/LmsCourseBuilder'));
const LmsLearnerAdmin = lazy(() => import('./lms/LmsLearnerAdmin'));
const LmsEnrolmentAdmin = lazy(() => import('./lms/LmsEnrolmentAdmin'));
const LmsMyLearning = lazy(() => import('./lms/LmsMyLearning'));

// Leave
const Leave = lazy(() => import('./leave/Leave'));
const LeaveAttendanceInsights = lazy(() => import('./leave/LeaveAttendanceInsights'));
const MyLeave = lazy(() => import('./leave').then(m => ({ default: m.MyLeave })));
const LeaveApprovals = lazy(() => import('./leave').then(m => ({ default: m.LeaveApprovals })));
const LeaveBalances = lazy(() => import('./leave').then(m => ({ default: m.LeaveBalances })));
const LeaveAdmin = lazy(() => import('./leave').then(m => ({ default: m.LeaveAdmin })));

// Vendors
const ApprovedVendorList = lazy(() => import('./procurement/vendors/ApprovedVendorList'));
const VendorPrequalList = lazy(() => import('./procurement/vendor-prequal/VendorPrequalList'));
const DormantVendors = lazy(() => import('./procurement/vendors/DormantVendors'));
const ProcurementSiteConfigPage = lazy(() => import('./procurement/sites/ProcurementSiteConfigPage'));
const VendorPrequalDetail = lazy(() => import('./procurement/vendor-prequal/VendorPrequalDetail'));

// Training board - archived 2025-10-21 (replaced by HR/HRWorkItemsPage.jsx)
// PSV module
const PSVDashboard = lazy(() => import('./mechanical/psv/PSVDashboard'));
const PSVReportForm = lazy(() => import('./mechanical/psv/PSVReportForm'));
const PSVReportList = lazy(() => import('./mechanical/psv/PSVReportList'));
const PSVReportDetail = lazy(() => import('./mechanical/psv/PSVReportDetail'));
const PSVImportPage = lazy(() => import('./mechanical/psv/PSVImportPage'));
const PSVConfigPage = lazy(() => import('./mechanical/psv/PSVConfigPage'));
const PSVInsightsDashboard = lazy(() => import('./mechanical/psv/PSVInsightsDashboard'));

// Valves module — sister to PSV. See docs/development/VALVES_MODULE_PLAN.md
const ValveDashboard = lazy(() => import('./mechanical/valves/ValveDashboard'));
const ValveReportList = lazy(() => import('./mechanical/valves/ValveReportList'));
const ValveReportDetail = lazy(() => import('./mechanical/valves/ValveReportDetail'));
const ValveReportForm = lazy(() => import('./mechanical/valves/ValveReportForm'));
const ValvesConfigPage = lazy(() => import('./mechanical/valves/ValvesConfigPage'));
const ValveInsightsDashboard = lazy(() => import('./mechanical/valves/ValveInsightsDashboard'));

// Clients
const ClientsList = lazy(() => import('./crm/ClientsList'));
const ClientsDetail = lazy(() => import('./crm/ClientsDetail'));
const CRMManager = lazy(() => import('./crm/CRMManager'));
const CRMClientsPage = lazy(() => import('./crm/CRMClientsPage'));
const TopOpportunitiesPage = lazy(() => import('./crm/TopOpportunitiesPage'));

// Mechanical (no React Router hooks)
const MechanicalMovementsList = lazy(() => import('./mechanical/movements/MechanicalMovementsList'));
const MechanicalNewBooking = lazy(() => import('./mechanical/movements/MechanicalNewBooking'));
const MechanicalMovementDetail = lazy(() => import('./mechanical/movements/MechanicalMovementDetail'));
const MechanicalItemDetail = lazy(() => import('./mechanical/movements/MechanicalItemDetail'));
const MechanicalScan = lazy(() => import('./mechanical/movements/MechanicalScan'));
const ValveHistory = lazy(() => import('./mechanical/movements/ValveHistory'));
const MechanicalNotificationConfig = lazy(() => import('./mechanical/MechanicalNotificationConfig'));
const MovementsInsightsDashboard = lazy(() => import('./mechanical/movements/insights/MovementsInsightsDashboard'));
const FlangeDashboard = lazy(() => import('./mechanical/flange/FlangeDashboard'));
const FlangeJointList = lazy(() => import('./mechanical/flange/FlangeJointList'));
import { FlangeScopeProvider } from './mechanical/flange/FlangeScopeContext';
const FlangeScopeSelector = lazy(() => import('./mechanical/flange/FlangeScopeSelector'));
const FlangeConfigPage = lazy(() => import('./mechanical/flange/FlangeConfigPage'));
const FlangeWorkPacks = lazy(() => import('./mechanical/flange/FlangeWorkPacks'));
const FlangeJointForm = lazy(() => import('./mechanical/flange/FlangeJointForm'));
const FlangeJointDetail = lazy(() => import('./mechanical/flange/FlangeJointDetail'));
const FlangeSiteMapViewer = lazy(() => import('./mechanical/flange/FlangeSiteMapViewer'));
const FlangeScanPage = lazy(() => import('./mechanical/flange/FlangeScanPage'));
const FlangePortal = lazy(() => import('./mechanical/flange/FlangePortal'));
const FlangeCalculator = lazy(() => import('./mechanical/flange/FlangeCalculator'));
const FlangeCampaigns = lazy(() => import('./mechanical/flange/FlangeCampaigns'));

// Training Dashboard
const TrainingCompetencyDashboard = lazy(() => import('./training/TrainingCompetencyDashboard'));
const TrainingBookings = lazy(() => import('./training/TrainingBookings'));
// CompetencyMatricesPage archived — functionality in MatrixConfigAdmin + MatrixEditor
const CompetencyAdmin = lazy(() => import('./training/CompetencyAdmin'));
const CertificateApprovalQueue = lazy(() => import('./training/CertificateApprovalQueue'));
const MyTrainingProfile = lazy(() => import('./training/MyTrainingProfile'));
const MatrixConfigAdmin = lazy(() => import('./training/MatrixConfigAdmin'));
// MatrixEditor now embedded as tab in MatrixConfigAdmin (Matrices page)
const GapAnalysis = lazy(() => import('./training/GapAnalysis'));
const ComplianceReport = lazy(() => import('./training/ComplianceReport'));

// Clients - Inpex
const InpexGateEntryDashboard = lazy(() => import('./clients/inpex/InpexGateEntryDashboard'));

// Electrical - EEHA
const EEHADashboard = lazy(() => import('./electrical/eeha/EEHADashboard'));
const EEHAEquipmentList = lazy(() => import('./electrical/eeha/EEHAEquipmentList'));
const EEHAEquipmentDetail = lazy(() => import('./electrical/eeha/EEHAEquipmentDetail'));
const EEHAEquipmentForm = lazy(() => import('./electrical/eeha/EEHAEquipmentForm'));
const EEHAInspectionList = lazy(() => import('./electrical/eeha/EEHAInspectionList'));
const EEHAInspectionDetail = lazy(() => import('./electrical/eeha/EEHAInspectionDetail'));
const EEHAInspectionForm = lazy(() => import('./electrical/eeha/EEHAInspectionForm'));
const EEHADefectList = lazy(() => import('./electrical/eeha/EEHADefectList'));
const EEHADefectDetail = lazy(() => import('./electrical/eeha/EEHADefectDetail'));
const EEHADefectForm = lazy(() => import('./electrical/eeha/EEHADefectForm'));
const EEHAScanPage = lazy(() => import('./electrical/eeha/EEHAScanPage'));
const EEHAConfigPage = lazy(() => import('./electrical/eeha/EEHAConfigPage'));
const EEHATagList = lazy(() => import('./electrical/eeha/EEHATagList'));
const EEHATagDetail = lazy(() => import('./electrical/eeha/EEHATagDetail'));
const EEHATagForm = lazy(() => import('./electrical/eeha/EEHATagForm'));
const EEHASiteMapViewer = lazy(() => import('./electrical/eeha/EEHASiteMapViewer'));
const EEHASiteMapUpload = lazy(() => import('./electrical/eeha/EEHASiteMapUpload'));
const EEHALotList = lazy(() => import('./electrical/eeha/EEHALotList'));
const EEHALotDetail = lazy(() => import('./electrical/eeha/EEHALotDetail'));
const EEHALotForm = lazy(() => import('./electrical/eeha/EEHALotForm'));
const EEHARiskAssessmentList = lazy(() => import('./electrical/eeha/EEHARiskAssessmentList'));
const EEHARiskAssessmentDetail = lazy(() => import('./electrical/eeha/EEHARiskAssessmentDetail'));
const EEHARiskAssessmentForm = lazy(() => import('./electrical/eeha/EEHARiskAssessmentForm'));
const EEHASampleList = lazy(() => import('./electrical/eeha/EEHASampleList'));
const EEHASampleGenerator = lazy(() => import('./electrical/eeha/EEHASampleGenerator'));
const EEHASampleDetail = lazy(() => import('./electrical/eeha/EEHASampleDetail'));
const EEHAWorkpackList = lazy(() => import('./electrical/eeha/EEHAWorkpackList'));
const EEHAWorkpackCreate = lazy(() => import('./electrical/eeha/EEHAWorkpackCreate'));
const EEHAWorkpackDetail = lazy(() => import('./electrical/eeha/EEHAWorkpackDetail'));
const EEHAOfflineSyncManager = lazy(() => import('./electrical/eeha/EEHAOfflineSyncManager'));
const EEHAInspectionGantt = lazy(() => import('./electrical/eeha/EEHAInspectionGantt'));
import { EEHAClientProvider } from './electrical/eeha/EEHAClientContext';
import Button from './shared/Button';
import LoadingSpinner from './shared/LoadingSpinner';
import ErrorBoundary from './shared/ErrorBoundary';
import ChunkLoadHealthy from './shared/ChunkLoadHealthy';
import MobileSpeedDial from './shared/MobileSpeedDial';
import PullToRefreshShell from './shared/PullToRefreshShell';
import { recoverFromChunkLoadError } from '../utils/chunkReload';
import type { PsvPrefill, RequestToEstimatePayload } from '../types/navigationPayloads';
import {
  approvalsDeskReturnUrl,
  readLocation,
  urlForView,
  writeLocation,
  type LocationIds,
} from '../utils/navigation';
import {
  extractRoleNames,
  canViewVendorList,
  isPlatformAdmin as isPlatformAdminGate,
  isMechanicalAdmin as isMechanicalAdminGate,
  isMechanicalClientTenant as isMechanicalClientTenantGate,
  isTrainingAdmin as isTrainingAdminGate,
  canOpenRoleCatalog as canOpenRoleCatalogGate,
  isNotificationsAdmin as isNotificationsAdminGate,
  canCostTransactionsReport as canCostTransactionsReportGate,
  canFinancePl as canFinancePlGate,
  canJobFinancials as canJobFinancialsGate,
  canJobsAdmin as canJobsAdminGate,
  canLearning as canLearningGate,
  canManageHomepageContent as canManageHomepageContentGate,
  canOwnTrainingProfile as canOwnTrainingProfileGate,
  canProcurementAdmin as canProcurementAdminGate,
  canTrainingAnalytics as canTrainingAnalyticsGate,
  canTrainingCertificates as canTrainingCertificatesGate,
  type RoleBearingUser,
} from '../utils/permissions';
import { TEXT } from './shared/Typography';

/* ------------------------------- types ------------------------------- */

/**
 * The signed-in user, as far as the router needs it. Role reading is delegated
 * to `RoleBearingUser` so this file cannot develop its own idea of what a role
 * is — that divergence is what let the Learning gate drift from the sidebar's.
 */
interface UserData extends RoleBearingUser {
  _id?: string;
  email?: string;
  name?: string;
  personCode?: string;
  employeeId?: string;
  isActive?: boolean;
}

interface SubmittedEstimate {
  id: string;
  estimateNumber: string;
}

interface SubmittedProReq {
  id: string;
  proReqNumber: string;
}

interface MainContentProps {
  user: UserData;
  token: string;
  activeItem: string;
  setActiveItem: (_item: string) => void;
  selectedRequestId: string | null;
  setSelectedRequestId: (_id: string | null) => void;
  selectedInvoiceId: string | null;
  setSelectedInvoiceId: (_id: string | null) => void;
  selectedJobId: string | null;
  setSelectedJobId: (_id: string | null) => void;
  selectedRateHeaderId: string | null;
  setActiveCategory: (_category: string | null) => void;
}

/**
 * Shown while a lazily-loaded view's chunk is in flight. Full-screen because a
 * route swap replaces the whole main region — an inline spinner would sit in a
 * blank page and read as a failed load.
 */
const ViewLoading = <LoadingSpinner fullScreen message="Loading..." />;

export default function MainContent({
  user,
  token,
  activeItem,
  setActiveItem,
  selectedRequestId,
  setSelectedRequestId,
  selectedInvoiceId,
  setSelectedInvoiceId,
  selectedJobId,
  setSelectedJobId,
  selectedRateHeaderId,
  setActiveCategory,
}: MainContentProps) {
  // Local selections
  // The Approval Desk needs approvals:desk (utils/permissions.ts), or a grant
  // the server made this session (hooks/useLandingProfile.ts). Anyone else who
  // reaches it by URL is moved to Home, without a history entry, so Back does
  // not return them to a page they cannot use. Without the role, the server is
  // asked fresh first: it grants the role to an approver who lacks it, such as
  // someone following their first approval email.
  const { allowed: deskAllowed, landing } = useApprovalsDeskAccess(
    token,
    extractRoleNames(user as RoleBearingUser)
  );
  useEffect(() => {
    if (activeItem !== 'ApprovalsDesk' || deskAllowed || landing.loading) return;
    if (!landing.fresh && token) {
      void recheckLandingProfile(token);
      return;
    }
    window.history.replaceState({}, '', urlForView('Home'));
    setActiveItem('Home');
  }, [activeItem, deskAllowed, landing.loading, landing.fresh, token, setActiveItem]);

  const [selectedRequisitionRef, setSelectedRequisitionRef] = useState<string | null>(null);
  const [selectedExpenseId, setSelectedExpenseId] = useState<string | null>(null);
  // Ephemeral root-rollup scope for the financial reports dashboard. When
  // null, the dashboard renders the single jobCode; when set, it aggregates
  // across these codes (root + subjobs).
  const [selectedJobAggCodes, setSelectedJobAggCodes] = useState<string[] | null>(null);
  // Job code carried into the Cost Transactions report by the "Cost
  // Transactions" button on JobDetail (prefill + auto-run).
  const [costReportJobCode, setCostReportJobCode] = useState<string | null>(null);
  // Project Controls — current WBS template being viewed/edited (null = new)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  // Project Controls — current proposal being viewed/edited
  const [selectedProposalId, setSelectedProposalId] = useState<string | null>(null);
  // Which list a proposal was opened from, so Back returns there (a live job lives under Jobs).
  const [proposalListView, setProposalListView] = useState<'Proposals' | 'ProjectJobs'>('Proposals');
  const [_selectedVendorCode, setSelectedVendorCode] = useState<string | null>(null);
  const [selectedVendorPrequalId, setSelectedVendorPrequalId] = useState<string | null>(null);

  // Estimates
  const [requestForEstimate, setRequestForEstimate] = useState<RequestToEstimatePayload | null>(
    null
  );
  const [selectedEstimateId, setSelectedEstimateId] = useState<string | null>(null);

  // Thank-you screens
  const [lastSubmittedEstimate, setLastSubmittedEstimate] = useState<SubmittedEstimate | null>(
    null
  );
  const [lastSubmittedProReq, setLastSubmittedProReq] = useState<SubmittedProReq | null>(null);

  // PSV
  const [selectedPsvId, setSelectedPsvId] = useState<string | null>(null);
  const [psvPrefill, setPsvPrefill] = useState<PsvPrefill | null>(null);
  const psvBackTarget = useRef<string>('PSVReportList');
  // True when the current PSVReportForm navigation came through go() (which
  // manages the report id deliberately). Sidebar navigation skips go(), so a
  // false value on arrival means any lingering id/prefill is stale and a NEW
  // blank test must be started.
  const psvFormNavViaGo = useRef(false);

  // Valves — same selection pattern as PSV. The id comes from the URL
  // ?id= query param when go() pushes a detail/form view.
  const [selectedValveId, setSelectedValveId] = useState<string | null>(null);

  // Clients
  const [selectedClientCode, setSelectedClientCode] = useState<string | null>(null);
  // Where a CRM Dashboard drill-down lands on the Clients page: a status filter, or one client.
  const [crmClientsStart, setCrmClientsStart] = useState<{ status?: string; clientId?: string; key: number }>({ key: 0 });
  // A drill-down's start state is for that one visit: leaving the Clients page
  // clears it, so the sidebar's own Clients entry opens the plain list again.
  useEffect(() => {
    if (activeItem !== 'PipelineClients') setCrmClientsStart(s => (s.status || s.clientId ? { key: s.key + 1 } : s));
  }, [activeItem]);

  // Mechanical selections
  const [selectedMovementId, setSelectedMovementId] = useState<string | null>(null);
  const [selectedMovementItemId, setSelectedMovementItemId] = useState<string | null>(null);
  const [selectedValveSerialNo, setSelectedValveSerialNo] = useState<string | null>(null);

  // EEHA selections
  const [selectedEEHAId, setSelectedEEHAId] = useState<string | null>(null);

  // One-shot flag: when the mobile speed-dial FAB requests "New Expense",
  // navigate to ExpensesDashboard and auto-open the 3-option create modal.
  const [pendingOpenExpenseUpload, setPendingOpenExpenseUpload] = useState(false);

  // Hide the mobile speed-dial FAB after an action is selected until the user
  // returns to the Home view (reset below via effect on activeItem).
  const [fabHidden, setFabHidden] = useState(false);
  useEffect(() => {
    if (activeItem === 'Home') setFabHidden(false);
  }, [activeItem]);

  // Learning (LMS) selections
  const [selectedLmsCourseId, setSelectedLmsCourseId] = useState<string | null>(null);

  // HR Employee selections
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [hrListFilters, setHrListFilters] = useState<Record<string, string> | undefined>(undefined);

  /**
   * Every role the user holds, lowercased, from all the shapes the API uses.
   * `currentUserForChildren` re-publishes it as `_computedRoles` because many
   * child screens read that field directly.
   */
  const userRoles = useMemo(() => extractRoleNames(user as RoleBearingUser), [user]);
  const currentUserForChildren = useMemo(
    () => ({ ...user, _computedRoles: userRoles }),
    [user, userRoles]
  );

  /**
   * View gates. Every one of these is imported from `utils/permissions`, which
   * the sidebar also uses — the sidebar decides whether a menu entry appears
   * and this file decides whether the view behind it renders, so a rule the two
   * derive separately eventually disagrees. It did: `canLearning` admitted
   * training administrators in the sidebar and not here, so they saw the whole
   * Learning menu and a blank page behind every entry in it.
   *
   * These gates stop a hand-typed `?view=X` rendering a screen the server will
   * refuse. They are not the security control — the backend enforces
   * independently on every request.
   */
  /**
   * The unrestricted System Administration view — platform administrators only.
   * Mechanical and training admins get their own restricted variants, ORed in at
   * the branch below.
   *
   * This used to admit `procurement_request:admin` as well, and it was the only
   * layer that did: `backend/routes/users.ts` and `roles.ts` both gate on
   * platform/mechanical/training admins and 403 that role on every call, and the
   * sidebar's `canUserAdmin` never offered it the menu entry. So the effect was
   * that typing `?view=UserManagement` rendered a screen where nothing loaded.
   * A frontend gate looser than the backend is still a bug.
   */
  const isUnrestrictedAdmin = useMemo(() => isPlatformAdminGate(userRoles), [userRoles]);
  const canProcurementAdmin = useMemo(() => canProcurementAdminGate(userRoles), [userRoles]);
  const isMechanicalAdmin = useMemo(() => isMechanicalAdminGate(userRoles), [userRoles]);
  const isTrainingAdmin = useMemo(() => isTrainingAdminGate(userRoles), [userRoles]);
  const canRoleCatalog = useMemo(() => canOpenRoleCatalogGate(userRoles), [userRoles]);
  const isNotificationsAdmin = useMemo(() => isNotificationsAdminGate(userRoles), [userRoles]);
  const isMechanicalClientTenant = useMemo(
    () => isMechanicalClientTenantGate(userRoles),
    [userRoles]
  );
  // An external client account has no Home: it is internal company content
  // (the server refuses it them too). Their landing screen is their PSV reports.
  useEffect(() => {
    if (activeItem !== 'Home' || !isMechanicalClientTenant) return;
    window.history.replaceState({}, '', urlForView('PSVReportList'));
    setActiveItem('PSVReportList');
  }, [activeItem, isMechanicalClientTenant, setActiveItem]);
  const canJobFinancials = useMemo(() => canJobFinancialsGate(userRoles), [userRoles]);
  const canFinancePl = useMemo(() => canFinancePlGate(userRoles), [userRoles]);
  const canCostTransactionsReport = useMemo(
    () => canCostTransactionsReportGate(userRoles),
    [userRoles]
  );
  const canJobsAdmin = useMemo(() => canJobsAdminGate(userRoles), [userRoles]);
  const canTrainingAnalytics = useMemo(() => canTrainingAnalyticsGate(userRoles), [userRoles]);
  const canTrainingCertificates = useMemo(
    () => canTrainingCertificatesGate(userRoles),
    [userRoles]
  );
  const canOwnTrainingProfile = useMemo(() => canOwnTrainingProfileGate(userRoles), [userRoles]);
  const canLearning = useMemo(() => canLearningGate(userRoles), [userRoles]);
  const canManageHomepageContent = useMemo(
    () => canManageHomepageContentGate(userRoles),
    [userRoles]
  );


  /**
   * The user shape the four standalone leave views expect. One memo rather than
   * the four identical inline copies this used to carry — they had to be edited
   * together and nothing said so.
   */
  const leaveUser = useMemo(
    () =>
      user
        ? {
            email: user.email,
            name: user.name,
            roles: userRoles,
            personCode: user.personCode || null,
          }
        : null,
    [user, userRoles]
  );

  /* ----------------------------- State <-> URL sync ---------------------------- */
  const syncingRef = useRef<boolean>(false);

  // Initial hydrate from URL on mount
  useEffect(() => {
    /**
     * Copy whatever the URL says into component state.
     *
     * The mount hydrate, the back/forward handler and the legacy hashchange
     * handler each carried a byte-identical copy of these twenty dispatches, so
     * adding one navigable id meant remembering all three places. It reads the
     * location at call time and only calls setters, which is what makes the
     * empty dependency array below correct rather than merely quiet.
     */
    const applyLocation = (): void => {
      const {
        view,
        cat,
        id,
        requestId,
        invoiceId,
        jobId,
        estimateId,
        expenseId,
        movementId,
        movementItemId,
        serialNo,
      } = readLocation();
      if (view) setActiveItem(view);
      if (typeof cat === 'string') setActiveCategory(cat || null);
      if (typeof id === 'string') setSelectedPsvId(id || null);
      if (view === 'PSVReportForm' && typeof id === 'string' && id) psvFormNavViaGo.current = true;
      if (typeof id === 'string' && cat === 'MechanicalValves') setSelectedValveId(id || null);
      if (typeof id === 'string' && cat === 'EEHA') setSelectedEEHAId(id || null);
      if (typeof id === 'string' && cat === 'Learning') setSelectedLmsCourseId(id || null);
      if (typeof id === 'string' && view?.startsWith('HREmployee')) setSelectedEmployeeId(id || null);
      if (typeof id === 'string' && view === 'ProposalDetail') setSelectedProposalId(id || null);
      if (typeof id === 'string' && view === 'WBSTemplateBuilder') setSelectedTemplateId(id || null);
      if (typeof id === 'string' && view === 'ClientDetail') setSelectedClientCode(id || null);
      if (typeof requestId === 'string') setSelectedRequestId(requestId || null);
      if (typeof invoiceId === 'string') setSelectedInvoiceId(invoiceId || null);
      if (typeof jobId === 'string') setSelectedJobId(jobId || null);
      if (typeof estimateId === 'string') setSelectedEstimateId(estimateId || null);
      if (typeof expenseId === 'string') setSelectedExpenseId(expenseId || null);
      if (typeof movementId === 'string') setSelectedMovementId(movementId || null);
      if (typeof movementItemId === 'string') setSelectedMovementItemId(movementItemId || null);
      if (typeof serialNo === 'string') setSelectedValveSerialNo(serialNo || null);
    };

    // Initial hydrate from the URL.
    applyLocation();
    // Back/forward (querystring)
    const onPop = () => {
      if (syncingRef.current) return;
      applyLocation();
    };
    window.addEventListener('popstate', onPop);

    // Legacy hashchange support (if user manually types #view=)
    const onHash = () => {
      if (syncingRef.current) return;
      applyLocation();
    };
    window.addEventListener('hashchange', onHash);

    // Bridge: convert-to-requisition flow on the procurement client-
    // request detail dispatches this event after the prefill has been
    // stashed in sessionStorage. We just navigate; RequisitionEntryForm
    // reads the sessionStorage key on mount.
    const onConvertToReq = () => {
      setActiveItem('RequisitionEntryForm');
      setActiveCategory('Requisitions');
    };
    window.addEventListener('NAV_TO_PROCREQ_NEW_REQUISITION', onConvertToReq as EventListener);

    // After a successful requisition submission the form dispatches
    // this event to send the user back to the list view.
    const onProcReqList = () => {
      setActiveItem('RequisitionList');
      setActiveCategory('Requisitions');
    };
    window.addEventListener('NAV_TO_PROCREQ_LIST', onProcReqList as EventListener);

    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('hashchange', onHash);
      window.removeEventListener('NAV_TO_PROCREQ_NEW_REQUISITION', onConvertToReq as EventListener);
      window.removeEventListener('NAV_TO_PROCREQ_LIST', onProcReqList as EventListener);
    };
    // Mount-only, and safe: every function registered above reads the location
    // at call time and then only calls state setters, whose identities React
    // guarantees are stable. Nothing is captured from the closure, so there is
    // no stale value to go out of date — adding dependencies would re-register
    // the window listeners on every render for no behavioural gain.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Navigation helper: set state + push new URL
  // Accepts explicit IDs to avoid stale state issues with React's async setState
  const go = (view: string, cat: string | null, opts: LocationIds = {}): void => {
    syncingRef.current = true;
    // Mark PSV-form navigations that flow through go() (edit / start-test /
    // duplicate). The sidebar "Start PSV Test" link bypasses go() entirely —
    // the effect below uses this flag to blank any stale report id on that path.
    if (view === 'PSVReportForm') psvFormNavViaGo.current = true;
    setActiveItem(view);
    if (typeof cat !== 'undefined') setActiveCategory(cat);
    if (typeof opts.id !== 'undefined') {
      setSelectedPsvId(opts.id || null);
      // Re-use the same `id` slot for Valve report navigation. PSV and
      // Valves never share a screen, so updating both here is safe and
      // saves wiring a parallel state pipeline.
      if (typeof view === 'string' && view.startsWith('Valve')) {
        setSelectedValveId(opts.id || null);
      }
    }
    writeLocation(
      {
        view,
        cat,
        id: opts.id,
        requestId: opts.requestId ?? selectedRequestId,
        invoiceId: opts.invoiceId ?? selectedInvoiceId,
        jobId: opts.jobId ?? selectedJobId,
        estimateId: opts.estimateId ?? selectedEstimateId,
        expenseId: opts.expenseId ?? selectedExpenseId,
        movementId: opts.movementId ?? selectedMovementId,
        movementItemId: opts.movementItemId ?? selectedMovementItemId,
        serialNo: opts.serialNo ?? selectedValveSerialNo,
      },
      { push: true }
    );
    Promise.resolve().then(() => {
      syncingRef.current = false;
    });
  };

  // Update just the category in-place (no history push)
  const _setCatOnly = (cat: string | null) => {
    syncingRef.current = true;
    setActiveCategory(cat);
    writeLocation(
      {
        view: activeItem,
        cat,
        id: selectedPsvId || '',
        requestId: selectedRequestId,
        invoiceId: selectedInvoiceId,
        jobId: selectedJobId,
        estimateId: selectedEstimateId,
        expenseId: selectedExpenseId,
        movementId: selectedMovementId,
        movementItemId: selectedMovementItemId,
        serialNo: selectedValveSerialNo,
      },
      { push: false }
    );
    Promise.resolve().then(() => {
      syncingRef.current = false;
    });
  };

  /* ----------------------------- Nav convenience ------------------------------ */
  // Back to the Approval Desk, on the queue the record was opened from (and the
  // period it was showing). The desk opens records with a full page load and
  // `from=approvals`, so that flag is the only trace of where the user came from.
  const backToApprovalsDesk = (queue: string) => {
    go('ApprovalsDesk', null);
    window.history.replaceState({}, '', approvalsDeskReturnUrl(queue));
    window.dispatchEvent(new Event('navigate-home'));
  };
  const openedFromApprovalsDesk = () =>
    new URLSearchParams(window.location.search).get('from') === 'approvals';

  /* ------------------------------- Select handlers ---------------------------- */
  const handleSelectRequest = (id: string) => {
    setSelectedRequestId(id);
    go('RequestDetail', 'Request', { requestId: id });
  };
  const handleSelectInvoice = (id: string) => {
    setSelectedInvoiceId(id);
    go('ClientInvoiceDetail', 'Invoices', { invoiceId: id });
  };
  const handleSelectJob = (id: string) => {
    setSelectedJobId(id);
    go('JobDetail', 'JobsSub', { jobId: id });
  };
  const handleCreateJob = () => {
    go('JobForm', 'Jobs');
  };
  const handleSelectEstimate = (id: string) => {
    setSelectedEstimateId(id);
    go('ProcurementEstimateDetail', 'Estimates', { estimateId: id });
  };
  const handleSelectRequisition = (ref: string) => {
    setSelectedRequisitionRef(ref);
    go('RequisitionDetail', 'Requisitions');
  };
  // The expenses screen an expense was opened from in this session, so "Back
  // to Expenses" returns there. Null after a reload or a link from outside,
  // when Back falls back to the Expenses dashboard.
  const expenseOpenedFrom = useRef<string | null>(null);
  const handleSelectExpense = (id: string) => {
    expenseOpenedFrom.current = activeItem;
    setSelectedExpenseId(id);
    go('ExpenseDetail', 'Expenses', { expenseId: id });
  };
  const handleSelectVendor = (code: string) => {
    setSelectedVendorCode(code);
    go('ApprovedVendorDetail', 'Vendors');
  };
  const handleSelectClient = (code: string) => {
    setSelectedClientCode(code);
    // Carry the code in the URL's generic id slot so refresh/back restores
    // the client instead of rendering the list payload as nonsense rows.
    go('ClientDetail', 'Clients', { id: code });
  };

  const handleBackToList = (type: string) => {
    if (type === 'invoices' && openedFromApprovalsDesk()) {
      backToApprovalsDesk('invoices');
    } else if (type === 'invoices') {
      // Use deep link URL for invoices to match App.tsx routing
      window.location.href = '/client-invoices';
    } else {
      // Use deep link URL for requests
      window.location.href = '/procurement/requests';
    }
  };

  // Request → Estimate
  const handleConvertRequestToEstimate = (request: RequestToEstimatePayload): void => {
    setRequestForEstimate(request);
    go('ProcurementEstimates', 'Estimates');
  };

  // Estimate submitted
  const handleEstimateSubmitted = ({ id, estimateNumber }: SubmittedEstimate) => {
    setLastSubmittedEstimate({ id, estimateNumber });
    go('EstimateSubmitted', 'Estimates');
  };

  /* ------------------------------- Mechanical nav ----------------------------- */
  const openMovement = (id: string) => {
    setSelectedMovementId(id);
    go('MechanicalMovementDetail', 'MechanicalMovements', { movementId: id });
  };
  const openMovementItem = (id: string) => {
    setSelectedMovementItemId(id);
    go('MechanicalItemDetail', 'MechanicalMovements', { movementItemId: id });
  };
  const openValveHistory = (sn: string) => {
    setSelectedValveSerialNo(sn);
    go('ValveHistory', 'MechanicalMovements', { serialNo: sn });
  };

  const startPSVTest = (prefillData: PsvPrefill): void => {
    setSelectedPsvId(null);
    setPsvPrefill(prefillData);
    psvBackTarget.current = 'MechanicalItemDetail';
    go('PSVReportForm', 'MechanicalPSV');
  };

  // "Start New Test" must ALWAYS be blank: when the PSV form is reached
  // without go() (i.e. the sidebar link after viewing an old report), any
  // lingering report id / prefill belongs to that old report — drop it.
  useEffect(() => {
    if (activeItem === 'PSVReportForm') {
      if (!psvFormNavViaGo.current) {
        setSelectedPsvId(null);
        setPsvPrefill(null);
      }
      psvFormNavViaGo.current = false;
    }
  }, [activeItem]);

  /* ================================== Render ================================== */
  return (
    <PullToRefreshProvider>
      <PullToRefreshShell>
        {/* `relative` is load-bearing: it makes the page the containing block for
            absolutely positioned descendants (every `sr-only` label among them).
            Without it they anchor outside this scroller, overflow the
            fixed-height shell, and a scrollIntoView (a Key Article link) scrolls
            the shell -- the top bar slides off-screen with no way back. */}
        <main className="relative min-h-0 flex-1 bg-gray-200 overflow-y-auto pb-safe-mobile overscroll-y-contain">
          {/* Every view below is code-split. One boundary rather than one per
              branch: only a single view is mounted at a time, so a shared
              fallback is what the user sees either way.

              The ErrorBoundary is load-bearing, not defensive dressing: a
              deploy prunes the previous build's chunks, so a tab left open
              overnight asks for a file that no longer exists. Without a
              boundary that rejected import unmounts the whole authenticated
              app. See utils/chunkReload.ts. */}
          <ErrorBoundary componentName="this view" onError={error => recoverFromChunkLoadError(error)}>
          <Suspense fallback={ViewLoading}>
          <ChunkLoadHealthy />
          <div>
            {/* Approval Desk — was the Home route until the split.

                Gated on approvals:desk (deskAllowed above), which the server
                grants to every approver, so approvers arriving from their own
                approval emails get in. The desk shows the viewer only their
                own queue, and every approve endpoint enforces authority. */}
            {activeItem === 'ApprovalsDesk' && deskAllowed && (
              <ApprovalsDesk
                token={token}
                onOpenEstimate={handleSelectEstimate}
              />
            )}

            {/* Home — the landing page for everyone. */}
            {activeItem === 'Home' && !isMechanicalClientTenant && (
              <Home token={token} onManageContent={() => go('HomepageContentAdmin', 'Settings')} />
            )}

            {/* User Settings / Profile */}
            {(activeItem === 'UserSettings' || activeItem === 'UserProfile') && <UserSettings />}

            {/* Client Requests */}
            {activeItem === 'ClientRequest' && (
              <ProcurementClientRequestForm
                onSubmitted={({ id, proReqNumber }) => {
                  setLastSubmittedProReq({ id, proReqNumber });
                  go('ClientRequestSubmitted', 'Request');
                }}
              />
            )}

            {activeItem === 'ClientRequestList' && (
              <ProcurementClientRequestList
                onSelectRequest={handleSelectRequest}
                onCreateEstimateFromRequest={handleConvertRequestToEstimate}
              />
            )}

            {activeItem === 'RequestDetail' && (
              <ProcurementClientRequestDetail
                token={token}
                currentUser={currentUserForChildren}
                requestId={selectedRequestId}
                onBack={() => handleBackToList('requests')}
              />
            )}

            {/* Estimates */}
            {activeItem === 'ProcurementEstimates' && (
              <ProcurementEstimates
                user={user}
                token={token}
                requestData={requestForEstimate}
                onSubmitted={handleEstimateSubmitted}
                onBack={() => go('ProcurementEstimatesList', 'Estimates')}
              />
            )}

            {activeItem === 'EstimateSubmitted' && (
              <div className="max-w-xl mx-auto border rounded-2xl bg-white shadow-sm p-6 text-center">
                <h1 className="text-2xl font-semibold">Thanks for submitting</h1>
                <p className="mt-2 text-gray-700">
                  Your estimate number is{' '}
                  <span className="font-mono font-semibold">
                    {lastSubmittedEstimate?.estimateNumber ?? '—'}
                  </span>
                  .
                </p>
                <div className="mt-6 flex justify-center gap-3">
                  <Button
                    variant="primary"
                    onClick={() => go('ProcurementEstimatesList', 'Estimates')}
                  >
                    Go to Estimates List
                  </Button>
                </div>
              </div>
            )}

            {activeItem === 'ClientRequestSubmitted' && (
              <div className="max-w-xl mx-auto border rounded-2xl bg-white shadow-sm p-6 text-center">
                <h1 className="text-2xl font-semibold">Thanks for submitting</h1>
                <p className="mt-2 text-gray-700">
                  Your Procurement Request Number is{' '}
                  <span className="font-mono font-semibold">
                    {lastSubmittedProReq?.proReqNumber ?? '—'}
                  </span>
                  .
                </p>
                <div className="mt-6 flex justify-center gap-3">
                  <Button variant="primary" onClick={() => go('ClientRequestList', 'Request')}>
                    Go to Procurement Request List
                  </Button>
                </div>
              </div>
            )}

            {activeItem === 'ProcurementEstimatesList' && (
              <ProcurementEstimatesList
                token={token}
                onSelectEstimate={handleSelectEstimate}
                onSelect={handleSelectEstimate}
              />
            )}

            {activeItem === 'ProcurementEstimateDetail' && (
              <ProcurementEstimateDetail
                estimateId={selectedEstimateId}
                user={user}
                onBack={() => go('ProcurementEstimatesList', 'Estimates')}
                onShowRequest={handleSelectRequest}
              />
            )}

            {/* Jobs */}
            {activeItem === 'JobForm' && (
              <JobForm
                onBack={() => go('JobList', 'Jobs')}
                onCreated={() => go('JobList', 'Jobs')}
              />
            )}

            {activeItem === 'JobList' && (
              <JobList onSelectJob={handleSelectJob} onCreateJob={handleCreateJob} user={user} />
            )}

            {activeItem === 'JobDetail' && (
              <JobDetail
                id={selectedJobId}
                onBack={() => go('JobList', 'JobsSub')}
                user={user}
                onOpenFinancials={
                  canJobFinancials
                    ? (jobCode: string) => {
                        setSelectedJobId(jobCode);
                        setSelectedJobAggCodes(null);
                        go('JobFinancialDashboard', 'Jobs', { jobId: jobCode });
                      }
                    : undefined
                }
                onOpenCostTransactions={
                  canCostTransactionsReport
                    ? (jobCode: string) => {
                        setCostReportJobCode(jobCode);
                        go('CostTransactionsReport', 'Reports');
                      }
                    : undefined
                }
              />
            )}

            {activeItem === 'JobsAdmin' && canJobsAdmin && <JobsAdmin />}

            {/* Invoices */}
            {activeItem === 'ClientInvoiceForm' && (
              <ClientInvoiceForm onSelectInvoice={handleSelectInvoice} />
            )}

            {activeItem === 'ClientInvoiceList' && (
              <ClientInvoiceList onSelectInvoice={handleSelectInvoice} />
            )}

            {activeItem === 'ClientInvoiceDetail' && (
              <ClientInvoiceDetail
                id={selectedInvoiceId}
                onBack={() => handleBackToList('invoices')}
              />
            )}

            {/* Rates */}
            {activeItem === 'Rates' && (
              <Rates user={user} token={token} onBack={() => go('Home', null)} />
            )}

            {/* Deep-link route for a single rate book (#/rate-detail?rateId=…).
                RatesDetail's prop is headerId — the old selectedRateHeaderId
                prop name never arrived and the page hung on a spinner. */}
            {activeItem === 'RateDetail' && selectedRateHeaderId && (
              <RatesDetail
                token={token}
                user={user}
                headerId={selectedRateHeaderId}
                onBack={() => go('Rates', 'ProjectControls')}
              />
            )}
            {activeItem === 'RateDetail' && !selectedRateHeaderId && (
              <Rates user={user} token={token} onBack={() => go('Home', null)} />
            )}

            {/* Expenses */}
            {activeItem === 'ExpenseEntryForm' && (
              <ExpenseEntryForm onSubmitted={() => go('ExpensesDashboard', 'Expenses')} />
            )}

            {activeItem === 'ExpenseList' && <ExpenseList onSelectExpense={handleSelectExpense} />}

            {activeItem === 'ExpensesDashboard' && (
              <ExpensesDashboard
                autoOpenUpload={pendingOpenExpenseUpload}
                onAutoOpenUploadHandled={() => setPendingOpenExpenseUpload(false)}
                onNavigate={(view, params) => {
                  if (view === 'ExpenseDetail' && params?.id) {
                    handleSelectExpense(params.id);
                  } else if (view === 'ExpenseEntryForm') {
                    go('ExpenseEntryForm', 'Expenses');
                  }
                }}
              />
            )}

            {activeItem === 'ExpenseApprovals' && (
              <ExpenseApprovals
                user={currentUserForChildren}
                token={token}
                onSelectExpense={handleSelectExpense}
              />
            )}

            {activeItem === 'ExpenseDetail' && (
              <ExpenseDetail
                id={selectedExpenseId}
                onBack={() => {
                  const from = new URLSearchParams(window.location.search).get('from');
                  if (from === 'approvals') {
                    backToApprovalsDesk('expenses');
                  } else if (from === 'admin') {
                    go('ExpenseAdmin', 'Expenses', { expenseId: '' });
                  } else {
                    go(expenseOpenedFrom.current ?? 'ExpensesDashboard', 'Expenses', { expenseId: '' });
                  }
                }}
              />
            )}

            {activeItem === 'ChartOfAccountsBrowser' && canJobFinancials && (
              <ChartOfAccountsBrowser />
            )}

            {activeItem === 'FinancePnlReport' && canFinancePl && <FinancePnlReport />}

            {activeItem === 'FinancialReportsHome' && canJobFinancials && (
              <FinancialReportsHome
                onOpenJob={(jobCode, aggCodes) => {
                  setSelectedJobId(jobCode);
                  // aggCodes is ephemeral (root-rollup mode) — kept in component
                  // state, not URL-encoded. Refreshing falls back to single-job
                  // view of the root, which is acceptable for now.
                  setSelectedJobAggCodes(aggCodes && aggCodes.length > 1 ? aggCodes : null);
                  go('JobFinancialDashboard', 'Jobs', { jobId: jobCode });
                }}
              />
            )}

            {activeItem === 'JobFinancialDashboard' && selectedJobId && canJobFinancials && (
              <JobFinancialDashboard
                jobCode={selectedJobId}
                aggCodes={selectedJobAggCodes ?? undefined}
                onBack={() => {
                  setSelectedJobAggCodes(null);
                  go('FinancialReportsHome', 'Jobs');
                }}
              />
            )}

            {/* Project Controls — WBS Templates */}
            {activeItem === 'WBSTemplates' && (
              <Templates
                user={user}
                onOpen={(id) => {
                  setSelectedTemplateId(id);
                  // Keep the id in the URL so a refresh reopens THIS template
                  // instead of silently becoming "New Template" (duplicate risk).
                  go('WBSTemplateBuilder', 'ProjectControls', { id });
                }}
                onCreate={() => {
                  setSelectedTemplateId(null);
                  go('WBSTemplateBuilder', 'ProjectControls', { id: '' });
                }}
                onBack={() => go('Home', null)}
              />
            )}

            {activeItem === 'WBSTemplateBuilder' && (
              <TemplateBuilder
                templateId={selectedTemplateId}
                user={user}
                onBack={() => go('WBSTemplates', 'ProjectControls')}
                onSaved={(id) => {
                  setSelectedTemplateId(id);
                  go('WBSTemplates', 'ProjectControls');
                }}
              />
            )}

            {/* Project Controls — Proposals (the sandbox) and Jobs (promoted proposals) */}
            {activeItem === 'Proposals' && (
              <Proposals
                user={user}
                onOpen={(id) => {
                  setSelectedProposalId(id);
                  setProposalListView('Proposals');
                  // id in the URL so refresh and shared links reopen the proposal.
                  go('ProposalDetail', 'ProjectControls', { id });
                }}
                onBack={() => go('Home', null)}
              />
            )}
            {activeItem === 'ProjectJobs' && (
              <Proposals
                user={user}
                mode="jobs"
                onOpen={(id) => {
                  setSelectedProposalId(id);
                  setProposalListView('ProjectJobs');
                  go('ProposalDetail', 'ProjectControls', { id });
                }}
                onBack={() => go('Home', null)}
              />
            )}

            {activeItem === 'ProposalDetail' && selectedProposalId && (
              <ProposalDetail
                proposalId={selectedProposalId}
                user={user}
                onBack={() => go(proposalListView, 'ProjectControls')}
                onOpenJobFinancials={
                  canJobFinancials
                    ? (jobCode) => {
                        setSelectedJobId(jobCode);
                        setSelectedJobAggCodes(null);
                        go('JobFinancialDashboard', 'Jobs', { jobId: jobCode });
                      }
                    : undefined
                }
              />
            )}

            {/* Expense Admin */}
            {activeItem === 'ExpenseAdmin' && <ExpenseAdmin />}

            {/* Receipt auto-match: health, match log, accuracy, manual run */}
            {activeItem === 'ReceiptAutoMatchDashboard' && <ReceiptAutoMatchDashboard />}

            {/* Shared Card AI suggestion log (shadow-mode review) */}
            {activeItem === 'SharedCardAiLog' && <SharedCardAiLog />}

            {/* Manual of Authority */}
            {activeItem === 'ManualOfAuthority' && <ManualOfAuthority />}

            {/* Procurement → Jobs: the Project Controls purchasing tracker, by job */}
            {activeItem === 'ProcurementJobs' && (
              <ProcurementJobs
                onOpenProposal={(id) => {
                  setSelectedProposalId(id);
                  setProposalListView('ProjectJobs');
                  go('ProposalDetail', 'ProjectControls', { id });
                }}
              />
            )}

            {/* Requisitions */}
            {activeItem === 'RequisitionEntryForm' && <RequisitionEntryForm />}

            {activeItem === 'RequisitionList' && (
              <PurchaseOrdersTabs
                currentUserName={user?.name || ''}
                currentUserEmail={user?.email || ''}
                isAdmin={canProcurementAdmin}
              />
            )}

            {activeItem === 'PurchaseOrdersTabs' && (
              <PurchaseOrdersTabs
                currentUserName={user?.name || ''}
                currentUserEmail={user?.email || ''}
                isAdmin={canProcurementAdmin}
              />
            )}

            {activeItem === 'PurchaseOrderApprovals' && (
              <PurchaseOrderApprovals user={user || null} />
            )}

            {activeItem === 'PurchaseOrderReceipting' && (
              <PurchaseOrderReceipting />
            )}

            {/* Reports */}
            {activeItem === 'HeadcountReport' && <HeadcountReport />}
            {activeItem === 'EARequirementsReport' && <EARequirementsReport />}
            {activeItem === 'MedicalsReport' && <MedicalsReport />}
            {activeItem === 'CostTransactionsReport' && canCostTransactionsReport && (
              <CostTransactionsReport initialJobCode={costReportJobCode || undefined} />
            )}

            {activeItem === 'RequisitionListLegacy' && (
              <RequisitionList onSelect={handleSelectRequisition} currentUserName={user?.name || ''} />
            )}

            {activeItem === 'RequisitionDetail' && (
              <RequisitionDetail
                reference={selectedRequisitionRef}
                onBack={() => go('RequisitionList', 'Requisitions')}
              />
            )}

            {/* HR */}
            {activeItem === 'HrOnboardingForm' && (
              <HrOnboardingForm setActiveItem={setActiveItem} />
            )}

            {activeItem === 'HrOnboardingList' && <HrOnboardingList />}

            {activeItem === 'HRWorkItems' && <HRWorkItemsPage user={user} />}

            {activeItem === 'HRPayslips' && <PayslipsSelfService />}

            {/* HR → Org Charts (uses dedicated component; isolated) */}
            {activeItem === 'OrgChart' && <OrgChart />}

            {/* HR → Employee Admin (profile transfer, export/import) — legacy, kept for backward compat */}
            {activeItem === 'HREmployeeAdmin' && (
              <HREmployeeAdmin user={user} token={token} />
            )}

            {/* HR → Employees List (new EEHA-style list) */}
            {activeItem === 'HREmployees' && (
              <HREmployeesList
                user={currentUserForChildren}
                token={token}
                initialFilters={hrListFilters}
                setActiveItem={(view: string, id?: string) => {
                  setSelectedEmployeeId(id || null);
                  go(view, null, id ? { id } : {});
                }}
              />
            )}

            {/* HR → Employee Detail (3 gated sections) */}
            {activeItem === 'HREmployeeDetail' && (
              <HREmployeeDetail
                user={currentUserForChildren}
                token={token}
                employeeId={selectedEmployeeId || undefined}
                setActiveItem={(view: string, id?: string) => {
                  setSelectedEmployeeId(id || null);
                  go(view, null, id ? { id } : {});
                }}
              />
            )}

            {/* HR → Admin (Profile Transfer, Export/Import) */}
            {activeItem === 'HRAdmin' && <HRAdmin user={user} token={token} />}

            {/* HR → Insights Dashboard */}
            {activeItem === 'HRInsights' && (
              <HRInsightsDashboard
                user={user}
                token={token}
                onNavigateToEmployees={(filters?: Record<string, string>) => {
                  setHrListFilters(filters);
                  go('HREmployees', null);
                }}
              />
            )}

            {/* HR → Performance (probation reviews) — the server refuses anyone else. */}
            {activeItem === 'HRPerformance' && <HRPerformance />}

            {/* HR → Sites & Positions */}
            {activeItem === 'HRSitesPositions' && (
              <HRSitesAndPositions user={user} token={token} />
            )}

            {/* Training Dashboard */}
            {activeItem === 'TrainingCompetencyDashboard' && (
              <TrainingCompetencyDashboard user={user} token={token} />
            )}

            {/* Training Bookings — queue and reports; the server scopes and gates both */}
            {activeItem === 'TrainingBookings' && <TrainingBookings />}

            {/* Competency Admin */}
            {activeItem === 'CompetencyAdmin' && <CompetencyAdmin user={user} token={token} />}

            {/* Certificate Approval Queue — the destination of the "Review in
                GRAutomates" link in the submission notification email. */}
            {activeItem === 'CertificateApprovalQueue' && canTrainingCertificates && (
              <CertificateApprovalQueue user={user} token={token} />
            )}

            {/* Own training profile — where the approve/reject emails send the
                employee, and where a rejection reason is read. */}
            {activeItem === 'MyTrainingProfile' && canOwnTrainingProfile && (
              <MyTrainingProfile user={user} token={token} />
            )}

            {/* Matrix Config Admin */}
            {activeItem === 'MatrixConfigAdmin' && (
              <MatrixConfigAdmin user={user} token={token} />
            )}

            {/* Gap Analysis — training_competency:admin / hr:admin / platform admin only */}
            {activeItem === 'GapAnalysis' && canTrainingAnalytics && (
              <GapAnalysis user={user} token={token} />
            )}

            {/* Compliance Report — training_competency:admin / hr:admin / platform admin only */}
            {activeItem === 'ComplianceReport' && canTrainingAnalytics && (
              <ComplianceReport user={user} token={token} />
            )}

            {/* Rosters */}
            {activeItem === 'Rosters' && <Rosters />}
            {activeItem === 'RostersDashboard' && <Rosters initialTab="myShifts" />}
            {activeItem === 'RostersAdmin' && <Rosters initialTab="assignments" />}
            {activeItem === 'RostersSwingAdmin' && <Rosters initialTab="admin" />}
            {activeItem === 'RostersManning' && <Rosters initialTab="manning" />}
            {activeItem === 'RostersViewer' && <Rosters initialTab="assignments" viewerMode />}

            {/* Timesheets - Consolidated page under Time & Pay */}
            {activeItem === 'TimesheetPage' && <TimesheetPage user={user} />}

            {/* Santos / Fieldglass admin */}
            {activeItem === 'SantosFGAdmin' && <SantosFGAdmin />}

            {/* Leave */}
            {activeItem === 'Leave' && <Leave />}
            {activeItem === 'LeaveAttendanceInsights' && <LeaveAttendanceInsights />}

            {/* Standalone Leave views */}
            {activeItem === 'MyLeaveStandalone' && (
              <div className="p-4 space-y-6">
                <div className="flex items-center gap-3">
                  <h2 className={TEXT.titleLg}>Leave Request</h2>
                </div>
                <MyLeave
                  user={leaveUser}
                />
              </div>
            )}
            {activeItem === 'LeaveApprovalsStandalone' && (
              <div className="p-4 space-y-6">
                <div className="flex items-center gap-3">
                  <h2 className={TEXT.titleLg}>Leave Approvals</h2>
                </div>
                <LeaveApprovals
                  user={leaveUser}
                />
              </div>
            )}
            {activeItem === 'LeaveBalancesStandalone' && (
              <div className="p-4 space-y-6">
                <div className="flex items-center gap-3">
                  <h2 className={TEXT.titleLg}>Leave Balances</h2>
                </div>
                <LeaveBalances
                  user={leaveUser}
                />
              </div>
            )}
            {activeItem === 'LeaveAdminStandalone' && (
              <div className="p-4 space-y-6">
                <div className="flex items-center gap-3">
                  <h2 className={TEXT.titleLg}>Leave Administration</h2>
                </div>
                <LeaveAdmin
                  user={leaveUser}
                />
              </div>
            )}

            {/* Settings → Public Holidays: platform admins only (the server refuses everyone else). */}
            {activeItem === 'PublicHolidaysAdmin' && isUnrestrictedAdmin && <PublicHolidaysAdmin />}

            {/* API Errors (Settings) */}
            {activeItem === 'ApiErrors' && (
              <div className="p-4 space-y-6">
                <div className="flex items-center gap-3">
                  <h2 className={TEXT.titleLg}>API Error Log</h2>
                </div>
                <ApiErrors
                  user={
                    user
                      ? {
                          email: user.email,
                          name: user.name,
                          roles: [
                            ...(user._computedRoles || []),
                            ...(user.roles || [])
                              .map(r => (typeof r === 'string' ? r : r?.name || ''))
                              .filter(Boolean),
                          ],
                        }
                      : null
                  }
                />
              </div>
            )}

            {/* Vendors — detail is rendered inline as a modal from the list */}
            {activeItem === 'ApprovedVendorList' && !isMechanicalClientTenant && canViewVendorList(userRoles) && (
              <ApprovedVendorList onSelectVendor={handleSelectVendor} />
            )}

            {/* Vendor Prequalification */}
            {activeItem === 'VendorPrequalList' && (
              <VendorPrequalList
                onSelect={(id: string) => {
                  setSelectedVendorPrequalId(id);
                  go('VendorPrequalDetail', 'Vendors', { id });
                }}
              />
            )}

            {activeItem === 'VendorPrequalDetail' && selectedVendorPrequalId && (
              <VendorPrequalDetail
                id={selectedVendorPrequalId}
                onBack={() => go('VendorPrequalList', 'Vendors')}
              />
            )}

            {/* Vendor lifecycle — dormant vendors + activation state. Its own page
                rather than a tab on Prequalification: it is about whether a vendor
                is usable at all, not about onboarding one. */}
            {activeItem === 'DormantVendors' && <DormantVendors />}

            {activeItem === 'ProcurementSiteConfig' && <ProcurementSiteConfigPage />}

            {/* Clients */}
            {(activeItem === 'ClientsList' || activeItem === 'Clients') && (
              <ClientsList onSelect={handleSelectClient} />
            )}

            {activeItem === 'ClientDetail' && selectedClientCode && (
              <ClientsDetail
                code={selectedClientCode}
                onBack={() => go('ClientsList', 'Clients')}
              />
            )}
            {activeItem === 'ClientDetail' && !selectedClientCode && (
              <ClientsList onSelect={handleSelectClient} />
            )}

            {/* CRM Dashboard */}
            {activeItem === 'CRMDashboard' && (
              <CRMManager
                onOpenProposal={(id) => {
                  setSelectedProposalId(id);
                  go('ProposalDetail', 'ProjectControls', { id });
                }}
                onManageOpportunities={() => go('TopOpportunities', 'CRM')}
                onOpenClients={status => {
                  setCrmClientsStart(s => ({ status, key: s.key + 1 }));
                  go('PipelineClients', 'CRM');
                }}
                onOpenClient={clientId => {
                  setCrmClientsStart(s => ({ clientId, key: s.key + 1 }));
                  go('PipelineClients', 'CRM');
                }}
              />
            )}

            {/* CRM Clients (replaces legacy PipelineClients) */}
            {activeItem === 'PipelineClients' && (
              <CRMClientsPage
                key={crmClientsStart.key}
                initialStatus={crmClientsStart.status}
                initialClientId={crmClientsStart.clientId}
                onOpenProposal={id => {
                  setSelectedProposalId(id);
                  go('ProposalDetail', 'ProjectControls', { id });
                }}
                onOpenJobFinancials={
                  canJobFinancials
                    ? (jobCode: string) => {
                        setSelectedJobId(jobCode);
                        setSelectedJobAggCodes(null);
                        go('JobFinancialDashboard', 'Jobs', { jobId: jobCode });
                      }
                    : undefined
                }
              />
            )}

            {/* Top Opportunities */}
            {activeItem === 'TopOpportunities' && <TopOpportunitiesPage />}

            {/* Admin */}
            {activeItem === 'UserManagement' && (isUnrestrictedAdmin || isMechanicalAdmin || isTrainingAdmin || isNotificationsAdmin) && (
              <UserManagement
                token={token}
                mechanicalAdminOnly={!isUnrestrictedAdmin && isMechanicalAdmin}
                trainingAdminOnly={!isUnrestrictedAdmin && !isMechanicalAdmin && isTrainingAdmin}
                notificationsAdminOnly={
                  !isUnrestrictedAdmin && !isMechanicalAdmin && !isTrainingAdmin && isNotificationsAdmin
                }
                /* Separate from the persona above. A training or mechanical
                   admin who is ALSO given notifications:admin keeps their own
                   scoped view, and must still get the Scheduled Jobs section
                   inside it — otherwise the role they were just granted
                   appears to do nothing. */
                canManageNotifications={isNotificationsAdmin}
              />
            )}

            {/* Also route Org Site Mapping menu to System Administration (UserManagement),
            because the Site Mapping editor lives there as a section. */}
            {activeItem === 'OrgSiteMappingAdmin' && isUnrestrictedAdmin && (
              <UserManagement token={token} />
            )}

            {activeItem === 'HomepageContentAdmin' && canManageHomepageContent && (
              <HomepageContentAdmin />
            )}

            {/* Bell sections and email audiences, with their on/off switches */}
            {activeItem === 'NotificationSettings' && isNotificationsAdmin && <NotificationSettings />}

            {/* Role Catalog — code-grounded per-role permissions + holders */}
            {activeItem === 'RoleCatalog' && canRoleCatalog && (
              <RoleCatalog />
            )}

            {/* PSV */}
            {activeItem === 'PSVDashboard' && (
              <PSVDashboard
                onOpenReport={report => {
                  const id = report?._id;
                  setSelectedPsvId(id);
                  psvBackTarget.current = 'PSVDashboard';
                  go('PSVReportDetail', 'MechanicalPSV', { id });
                }}
              />
            )}

            {activeItem === 'PSVReportForm' && (
              <PSVReportForm
                key={selectedPsvId || 'new'}
                id={selectedPsvId}
                prefill={psvPrefill}
                onSaved={r => {
                  const newId = r?._id;
                  setSelectedPsvId(newId);
                  setPsvPrefill(null);
                  go('PSVReportDetail', 'MechanicalPSV', { id: newId });
                }}
                onCancel={() => {
                  setPsvPrefill(null);
                  go('Home', null, { id: '' });
                }}
              />
            )}

            {activeItem === 'PSVReportList' && (
              <PSVReportList
                onOpen={id => {
                  setSelectedPsvId(id);
                  psvBackTarget.current = 'PSVReportList';
                  go('PSVReportDetail', 'MechanicalPSV', { id });
                }}
                onDuplicate={prefillData => {
                  setSelectedPsvId(null);
                  setPsvPrefill(prefillData);
                  psvBackTarget.current = 'PSVReportList';
                  go('PSVReportForm', 'MechanicalPSV', { id: '' });
                }}
              />
            )}

            {activeItem === 'PSVReportDetail' && (
              <PSVReportDetail
                id={selectedPsvId}
                onBack={() => go(psvBackTarget.current, 'MechanicalPSV', { id: '' })}
                onEdit={report => {
                  const id = report?._id || selectedPsvId || '';
                  setSelectedPsvId(id);
                  go('PSVReportForm', 'MechanicalPSV', { id });
                }}
                currentUser={user}
              />
            )}

            {activeItem === 'PSVConfigPage' && <PSVConfigPage user={user} />}

            {activeItem === 'PSVImportPage' && (
              <PSVImportPage onClose={() => go('PSVReportList', 'MechanicalPSV', {})} />
            )}

            {activeItem === 'PSVInsightsDashboard' && (
              <PSVInsightsDashboard
                onBack={() => go('PSVReportList', 'MechanicalPSV', {})}
                onOpenReport={id => {
                  psvBackTarget.current = 'PSVInsightsDashboard';
                  go('PSVReportDetail', 'MechanicalPSV', { id });
                }}
              />
            )}

            {/* Valves module — sister to PSV. Phase 1 mounts the basic
            CRUD pages; insights dashboard arrives in Phase 3. */}
            {activeItem === 'ValveDashboard' && (
              <ValveDashboard onOpen={id => go('ValveReportDetail', 'MechanicalValves', { id })} />
            )}
            {activeItem === 'ValveReportList' && (
              <ValveReportList
                onOpen={id => go('ValveReportDetail', 'MechanicalValves', { id })}
                onNew={() => go('ValveReportForm', 'MechanicalValves', {})}
              />
            )}
            {activeItem === 'ValveReportDetail' && (
              <ValveReportDetail
                reportId={selectedValveId || ''}
                onEdit={id => go('ValveReportForm', 'MechanicalValves', { id })}
                onBack={() => go('ValveReportList', 'MechanicalValves', { id: '' })}
              />
            )}
            {activeItem === 'ValveReportForm' && (
              <ValveReportForm
                reportId={selectedValveId || undefined}
                onSaved={id => id && go('ValveReportDetail', 'MechanicalValves', { id })}
                onCancel={() => go('ValveReportList', 'MechanicalValves', { id: '' })}
              />
            )}
            {activeItem === 'ValvesConfigPage' && <ValvesConfigPage />}
            {activeItem === 'ValveInsightsDashboard' && <ValveInsightsDashboard onOpen={id => go('ValveReportDetail', 'MechanicalValves', { id })} />}

            {/* Mechanical */}
            {activeItem === 'MechanicalMovementsList' && (
              <MechanicalMovementsList
                onOpenMovement={openMovement}
                onOpenItem={openMovementItem}
                onCreateNew={() => go('MechanicalNewBooking', 'MechanicalMovements')}
              />
            )}

            {activeItem === 'MechanicalNewBooking' && (
              <MechanicalNewBooking
                onSaved={openMovement}
                onCancel={() => go('MechanicalMovementsList', 'MechanicalMovements')}
              />
            )}

            {activeItem === 'MechanicalMovementDetail' && (
              <MechanicalMovementDetail
                id={selectedMovementId}
                onOpenItem={openMovementItem}
                onBack={() => go('MechanicalMovementsList', 'MechanicalMovements')}
              />
            )}

            {activeItem === 'MechanicalItemDetail' && (
              <MechanicalItemDetail
                itemId={selectedMovementItemId}
                onViewValveHistory={openValveHistory}
                onStartPSVTest={startPSVTest}
                onOpenItem={openMovementItem}
              />
            )}

            {activeItem === 'MechanicalScan' && <MechanicalScan onFound={openMovementItem} />}

            {activeItem === 'ValveHistory' && selectedValveSerialNo && (
              <ValveHistory
                serialNo={selectedValveSerialNo}
                onBack={() => go('MechanicalMovementsList', 'MechanicalMovements')}
              />
            )}

            {activeItem === 'MovementsInsightsDashboard' && (
              <MovementsInsightsDashboard
                onBack={() => go('MechanicalMovementsList', 'MechanicalMovements', {})}
                onOpenMovement={id =>
                  go('MechanicalMovementDetail', 'MechanicalMovements', { movementId: id })
                }
                onOpenItem={id =>
                  go('MechanicalItemDetail', 'MechanicalMovements', { movementItemId: id })
                }
              />
            )}

            {activeItem === 'MechanicalNotificationConfig' && <MechanicalNotificationConfig />}

            {/* Flange Management — one provider wraps all flange pages so the
                Client/Site/Plant scope persists as the user navigates. */}
            {activeItem.startsWith('Flange') && (
              <FlangeScopeProvider>
                <FlangeScopeSelector />

                {activeItem === 'FlangeDashboard' && (
                  <FlangeDashboard onOpenList={() => go('FlangeJointList', 'MechanicalFlange', {})} />
                )}

                {activeItem === 'FlangeJointList' && (
                  <FlangeJointList
                    onOpenJoint={id => go('FlangeJointDetail', 'MechanicalFlange', { id })}
                    onNew={() => go('FlangeJointForm', 'MechanicalFlange', { id: '' })}
                    onOpenMap={() => go('FlangeSiteMapViewer', 'MechanicalFlange', {})}
                    onScan={() => go('FlangeScanPage', 'MechanicalFlange', {})}
                  />
                )}

                {activeItem === 'FlangeJointForm' && (
                  <FlangeJointForm
                    jointId={selectedPsvId || undefined}
                    onSaved={id => go('FlangeJointDetail', 'MechanicalFlange', { id })}
                    onCancel={() => go('FlangeJointList', 'MechanicalFlange', {})}
                  />
                )}

                {activeItem === 'FlangeJointDetail' && selectedPsvId && (
                  <FlangeJointDetail
                    jointId={selectedPsvId}
                    onEdit={id => go('FlangeJointForm', 'MechanicalFlange', { id })}
                    onBack={() => go('FlangeJointList', 'MechanicalFlange', {})}
                  />
                )}

                {activeItem === 'FlangeSiteMapViewer' && (
                  <FlangeSiteMapViewer
                    onOpenJoint={id => go('FlangeJointDetail', 'MechanicalFlange', { id })}
                    onBack={() => go('FlangeJointList', 'MechanicalFlange', {})}
                  />
                )}

                {activeItem === 'FlangeScanPage' && (
                  <FlangeScanPage onFound={id => go('FlangeJointDetail', 'MechanicalFlange', { id })} />
                )}

                {activeItem === 'FlangePortal' && <FlangePortal />}

                {activeItem === 'FlangeCalculator' && <FlangeCalculator />}

                {activeItem === 'FlangeCampaigns' && (
                  <FlangeCampaigns onOpenJoint={id => go('FlangeJointDetail', 'MechanicalFlange', { id })} />
                )}

                {activeItem === 'FlangeWorkPacks' && (
                  <FlangeWorkPacks onOpenJoint={id => go('FlangeJointDetail', 'MechanicalFlange', { id })} />
                )}

                {activeItem === 'FlangeConfig' && <FlangeConfigPage />}
              </FlangeScopeProvider>
            )}

            {/* Clients - Inpex */}
            {activeItem === 'InpexGateEntry' && <InpexGateEntryDashboard />}

            {/* Electrical - EEHA - Single provider wraps all EEHA pages for shared state */}
            {activeItem.startsWith('EEHA') && (
              <EEHAClientProvider>
                {activeItem === 'EEHADashboard' && (
                  <EEHADashboard
                    user={user}
                    token={token}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {activeItem === 'EEHAEquipmentList' && (
                  <EEHAEquipmentList
                    user={user}
                    token={token}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {activeItem === 'EEHAEquipmentDetail' && (
                  <EEHAEquipmentDetail
                    user={user}
                    token={token}
                    equipmentId={selectedEEHAId || undefined}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {activeItem === 'EEHAEquipmentForm' && (
                  <EEHAEquipmentForm
                    user={user}
                    token={token}
                    equipmentId={selectedEEHAId || undefined}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {activeItem === 'EEHAInspectionList' && (
                  <EEHAInspectionList
                    user={user}
                    token={token}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {activeItem === 'EEHAInspectionDetail' && (
                  <EEHAInspectionDetail
                    user={user}
                    token={token}
                    inspectionId={selectedEEHAId || undefined}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {activeItem === 'EEHAInspectionForm' && (
                  <EEHAInspectionForm
                    user={user}
                    token={token}
                    inspectionId={selectedEEHAId || undefined}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {activeItem === 'EEHADefectList' && (
                  <EEHADefectList
                    user={user}
                    token={token}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {activeItem === 'EEHADefectDetail' && (
                  <EEHADefectDetail
                    user={user}
                    token={token}
                    defectId={selectedEEHAId || undefined}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {activeItem === 'EEHADefectForm' && (
                  <EEHADefectForm
                    user={user}
                    token={token}
                    defectId={selectedEEHAId || undefined}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {activeItem === 'EEHAScanPage' && (
                  <EEHAScanPage
                    user={user}
                    token={token}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {activeItem === 'EEHAConfigPage' && <EEHAConfigPage user={user} token={token} />}

                {activeItem === 'EEHATagList' && (
                  <EEHATagList
                    user={user}
                    token={token}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {activeItem === 'EEHATagDetail' && (
                  <EEHATagDetail
                    user={user}
                    token={token}
                    tagId={selectedEEHAId}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {activeItem === 'EEHATagForm' && (
                  <EEHATagForm
                    user={user}
                    token={token}
                    tagId={selectedEEHAId}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {activeItem === 'EEHASiteMapViewer' && (
                  <EEHASiteMapViewer
                    user={user}
                    token={token}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {activeItem === 'EEHASiteMapUpload' && (
                  <EEHASiteMapUpload
                    user={user}
                    token={token}
                    itemId={selectedEEHAId}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {activeItem === 'EEHALotList' && (
                  <EEHALotList
                    user={user}
                    token={token}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {activeItem === 'EEHALotDetail' && (
                  <EEHALotDetail
                    user={user}
                    token={token}
                    lotId={selectedEEHAId}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {activeItem === 'EEHALotAddTags' && (
                  <EEHALotDetail
                    user={user}
                    token={token}
                    lotId={selectedEEHAId}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {activeItem === 'EEHALotForm' && (
                  <EEHALotForm
                    user={user}
                    token={token}
                    lotId={selectedEEHAId || undefined}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {/* Risk Assessment Components */}
                {activeItem === 'EEHARiskAssessmentList' && (
                  <EEHARiskAssessmentList
                    setActiveItem={(view: string) => {
                      go(view, 'EEHA', {});
                    }}
                    setSelectedRiskAssessmentId={setSelectedEEHAId}
                  />
                )}

                {activeItem === 'EEHARiskAssessmentDetail' && (
                  <EEHARiskAssessmentDetail
                    user={user}
                    token={token}
                    assessmentId={selectedEEHAId}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {activeItem === 'EEHARiskAssessmentForm' && (
                  <EEHARiskAssessmentForm
                    user={user}
                    token={token}
                    assessmentId={selectedEEHAId}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {/* Sample Components */}
                {activeItem === 'EEHASampleList' && (
                  <EEHASampleList
                    setActiveItem={(view: string) => {
                      go(view, 'EEHA', {});
                    }}
                    setSelectedSampleId={setSelectedEEHAId}
                    setSelectedLotId={setSelectedEEHAId}
                  />
                )}

                {activeItem === 'EEHASampleGenerator' && (
                  <EEHASampleGenerator
                    setActiveItem={(view: string) => {
                      go(view, 'EEHA', {});
                    }}
                    setSelectedSampleId={setSelectedEEHAId}
                    preselectedLotId={selectedEEHAId}
                  />
                )}

                {activeItem === 'EEHASampleDetail' && (
                  <EEHASampleDetail
                    sampleId={selectedEEHAId || ''}
                    setActiveItem={(view: string) => {
                      go(view, 'EEHA', {});
                    }}
                    setSelectedTagId={setSelectedEEHAId}
                    setSelectedInspectionId={setSelectedEEHAId}
                    setSelectedSampleId={setSelectedEEHAId}
                  />
                )}

                {/* Workpack Components */}
                {activeItem === 'EEHAWorkpackList' && (
                  <EEHAWorkpackList
                    onNavigate={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', {});
                    }}
                  />
                )}

                {activeItem === 'EEHAWorkpackCreate' && (
                  <EEHAWorkpackCreate
                    onNavigate={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', {});
                    }}
                  />
                )}

                {activeItem === 'EEHAWorkpackDetail' && (
                  <EEHAWorkpackDetail
                    workpackId={selectedEEHAId || ''}
                    onNavigate={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', {});
                    }}
                  />
                )}

                {activeItem === 'EEHAInspectionGantt' && (
                  <EEHAInspectionGantt
                    user={user}
                    token={token}
                    setActiveItem={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', id ? { id } : {});
                    }}
                  />
                )}

                {activeItem === 'EEHAOfflineSyncManager' && (
                  <EEHAOfflineSyncManager
                    onNavigate={(view: string, id?: string) => {
                      setSelectedEEHAId(id || null);
                      go(view, 'EEHA', {});
                    }}
                  />
                )}
              </EEHAClientProvider>
            )}

            {/* Learning (LMS) */}
            {canLearning && activeItem === 'LmsCourseAdmin' && (
              <LmsCourseAdmin
                setActiveItem={(view: string, id?: string) => {
                  setSelectedLmsCourseId(id || null);
                  go(view, 'Learning', id ? { id } : {});
                }}
              />
            )}

            {canLearning && activeItem === 'LmsCourseDetail' && (
              <LmsCourseDetail
                courseId={selectedLmsCourseId || ''}
                setActiveItem={(view: string, id?: string) => {
                  setSelectedLmsCourseId(id || null);
                  go(view, 'Learning', id ? { id } : {});
                }}
              />
            )}

            {canLearning && activeItem === 'LmsCoursePlayer' && (
              <LmsCoursePlayer
                /*
                 * My Learning launches the player as "courseId|enrolmentId",
                 * because the enrolment is what makes the sitting count rather
                 * than a preview. `setActiveItem` carries one id across the whole
                 * app, so the pair travels in it rather than threading a second
                 * parameter through every caller in the module. A bare id is an
                 * author previewing, which is exactly the older behaviour.
                 */
                courseId={(selectedLmsCourseId || '').split('|')[0]}
                enrolmentId={(selectedLmsCourseId || '').split('|')[1] || undefined}
                setActiveItem={(view: string, id?: string) => {
                  setSelectedLmsCourseId(id || null);
                  go(view, 'Learning', id ? { id } : {});
                }}
              />
            )}

            {canLearning && activeItem === 'LmsMyLearning' && (
              <LmsMyLearning
                setActiveItem={(view: string, id?: string) => {
                  setSelectedLmsCourseId(id || null);
                  go(view, 'Learning', id ? { id } : {});
                }}
              />
            )}

            {canLearning && activeItem === 'LmsCourseBuilder' && (
              <LmsCourseBuilder
                courseId={selectedLmsCourseId || ''}
                setActiveItem={(view: string, id?: string) => {
                  setSelectedLmsCourseId(id || null);
                  go(view, 'Learning', id ? { id } : {});
                }}
              />
            )}

            {canLearning && activeItem === 'LmsLearnerAdmin' && (
              <LmsLearnerAdmin
                setActiveItem={(view: string, id?: string) => {
                  setSelectedLmsCourseId(id || null);
                  go(view, 'Learning', id ? { id } : {});
                }}
              />
            )}

            {canLearning && activeItem === 'LmsEnrolmentAdmin' && (
              <LmsEnrolmentAdmin
                setActiveItem={(view: string, id?: string) => {
                  setSelectedLmsCourseId(id || null);
                  go(view, 'Learning', id ? { id } : {});
                }}
              />
            )}

            {canLearning && activeItem === 'LmsAdmin' && (
              <LmsAdmin
                setActiveItem={(view: string, id?: string) => {
                  setSelectedLmsCourseId(id || null);
                  go(view, 'Learning', id ? { id } : {});
                }}
              />
            )}
          </div>
          </Suspense>
          </ErrorBoundary>

          {/* Mobile speed-dial FAB — New Timesheet / New Expense.
              Hidden after a selection until the user returns to Home. */}
          {!fabHidden && (
            <MobileSpeedDial
              onSelect={action => {
                setFabHidden(true);
                if (action === 'NewExpense') {
                  setPendingOpenExpenseUpload(true);
                  go('ExpensesDashboard', 'Expenses');
                } else if (action === 'NewTimesheet') {
                  go('TimesheetPage', null);
                }
              }}
            />
          )}
        </main>
      </PullToRefreshShell>
    </PullToRefreshProvider>
  );
}

